import React from 'react';
import { WebsiteDesignProps } from './types';
import { Button } from "@/components/ui/button";
import { Sparkles, Moon, Telescope, Satellite, Rocket, Calendar, ArrowRight, Star } from 'lucide-react';

const AstronomyObservatory: React.FC<WebsiteDesignProps> = ({ previewData }) => {
    const schoolName = previewData?.name || "Starlight Observatory";
    
    return (
        <div className="min-h-screen bg-black font-sans text-white selection:bg-white selection:text-black overflow-x-hidden relative">
            
            {/* Starfield Background */}
            <div className="fixed inset-0 z-0">
                {[...Array(50)].map((_, i) => (
                    <div 
                        key={i}
                        className="absolute bg-white rounded-full animate-pulse"
                        style={{
                            top: `${Math.random() * 100}%`,
                            left: `${Math.random() * 100}%`,
                            width: `${Math.random() * 2 + 1}px`,
                            height: `${Math.random() * 2 + 1}px`,
                            opacity: Math.random() * 0.7 + 0.3,
                            animationDelay: `${Math.random() * 5}s`,
                            animationDuration: `${Math.random() * 3 + 2}s`
                        }}
                    />
                ))}
            </div>
            
            {/* Nebula Glow */}
            <div className="fixed top-[-20%] right-[-10%] w-[800px] h-[800px] bg-blue-900/20 blur-[150px] rounded-full z-0 pointer-events-none"></div>
            <div className="fixed bottom-[-20%] left-[-10%] w-[600px] h-[600px] bg-purple-900/20 blur-[150px] rounded-full z-0 pointer-events-none"></div>

            {/* Navigation */}
            <nav className="fixed w-full z-50 px-8 py-8 backdrop-blur-sm bg-black/20 border-b border-white/5">
                <div className="max-w-7xl mx-auto flex justify-between items-center">
                    <div className="flex items-center gap-3">
                         <div className="w-10 h-10 border border-white/20 rounded-full flex items-center justify-center backdrop-blur-md">
                            <Moon size={18} className="text-white fill-white" />
                         </div>
                         <span className="text-lg font-light tracking-[0.2em] uppercase">{schoolName}</span>
                    </div>
                    
                    <div className="hidden md:flex gap-12 text-xs font-bold uppercase tracking-[0.15em] text-white/60">
                         <a href="#" className="hover:text-white transition-colors hover:shadow-[0_0_10px_white] hover:shadow-white/50">Visit</a>
                         <a href="#" className="hover:text-white transition-colors hover:shadow-[0_0_10px_white] hover:shadow-white/50">Telescope</a>
                         <a href="#" className="hover:text-white transition-colors hover:shadow-[0_0_10px_white] hover:shadow-white/50">Learn</a>
                    </div>
                    
                    <Button variant="outline" className="border-white/30 hover:bg-white hover:text-black rounded-full px-6 h-10 text-xs font-bold uppercase tracking-widest transition-all">
                        Book Session
                    </Button>
                </div>
            </nav>

            {/* Hero Section */}
            <header className="relative z-10 min-h-screen flex items-center justify-center px-6">
                <div className="text-center max-w-4xl mx-auto">
                    <div className="inline-block mb-8 animate-bounce delay-1000">
                        <Star size={12} className="text-blue-400 fill-blue-400 mx-auto" />
                    </div>
                    <h1 className="text-5xl md:text-8xl font-thin tracking-tighter mb-8 leading-[1.1] mix-blend-screen">
                        Look <span className="font-serif italic text-blue-300">Up</span>. <br/>
                        The Universe is Waiting.
                    </h1>
                    <p className="text-white/50 text-lg md:text-xl font-light max-w-xl mx-auto mb-12 leading-relaxed">
                        We provide a window to the cosmos. Experience the wonders of deep space through our world-class observatory and educational programs.
                    </p>
                    
                    <div className="flex flex-col md:flex-row items-center justify-center gap-6">
                        <Button className="bg-white text-black hover:bg-blue-50 rounded-full px-10 h-14 text-sm font-bold uppercase tracking-widest hover:scale-105 transition-transform">
                            View Sky Map
                        </Button>
                        <span className="text-xs font-mono text-white/30 uppercase tracking-widest">or</span>
                        <a href="#" className="text-sm font-bold uppercase tracking-widest border-b border-white/30 pb-1 hover:text-blue-300 hover:border-blue-300 transition-colors">
                            Join Tonight's Stream
                        </a>
                    </div>
                </div>
                
                {/* Scroll Indicator */}
                <div className="absolute bottom-12 left-1/2 transform -translate-x-1/2 flex flex-col items-center gap-2 opacity-50">
                    <span className="text-[10px] uppercase tracking-widest">Scroll to Explore</span>
                    <div className="w-[1px] h-12 bg-gradient-to-b from-white to-transparent"></div>
                </div>
            </header>

            {/* Features (Floating Circles) */}
            <section className="relative z-10 py-32 px-6">
                <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-8">
                     {[
                        { icon: Telescope, title: "24\" Reflector", desc: "Our primary instrument for deep sky observation." },
                        { icon: Rocket, title: "Space Camp", desc: "Summer programs for aspiring astronauts." },
                        { icon: Satellite, title: "S.E.T.I. Node", desc: "Analyzing radio signals for extraterrestrial life." },
                     ].map((item, i) => (
                         <div key={i} className="group relative aspect-square rounded-full border border-white/10 hover:border-white/30 bg-white/5 backdrop-blur-md flex flex-col items-center justify-center p-8 transition-all duration-500 hover:bg-white/10">
                             <item.icon size={32} strokeWidth={1} className="mb-6 text-blue-300 group-hover:scale-110 transition-transform" />
                             <h3 className="text-xl font-light uppercase tracking-widest mb-2 text-center">{item.title}</h3>
                             <p className="text-white/40 text-sm text-center font-light leading-relaxed">{item.desc}</p>
                         </div>
                     ))}
                </div>
            </section>

             {/* Footer */}
            <footer className="relative z-10 border-t border-white/10 py-20 px-6 bg-black">
                 <div className="max-w-7xl mx-auto flex flex-col items-center text-center gap-8">
                     <div className="w-12 h-12 border border-white/20 rounded-full flex items-center justify-center">
                        <Moon size={20} className="text-white" />
                     </div>
                     
                     <div className="grid grid-cols-1 md:grid-cols-3 gap-12 md:gap-24 text-xs font-bold uppercase tracking-[0.2em] text-white/40">
                         <a href="#" className="hover:text-white transition-colors">Coordinates</a>
                         <a href="#" className="hover:text-white transition-colors">Dark Sky Policy</a>
                         <a href="#" className="hover:text-white transition-colors">Membership</a>
                     </div>
                     
                     <p className="text-[10px] text-white/20 mt-12 font-mono">
                         &copy; 2026 {schoolName}. All systems nominal.
                     </p>
                 </div>
            </footer>
        </div>
    );
};

export default AstronomyObservatory;
